import { Injectable, inject } from '@angular/core';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import { filterNil } from '@ngneat/elf';
import { TranslocoService } from '@ngneat/transloco';
import { ShepherdService } from 'angular-shepherd';
import { combineLatest, retry, take } from 'rxjs';
import type { StepOptions, StepOptionsButton } from 'shepherd.js';
import { MAX_PRODUCTS_EXPORT_NUMBER } from 'src/app/defaults/product.defaults';
import { CountryRepository } from 'src/app/entities/countries/state/country.repository';

@Injectable({
  providedIn: 'root',
})
export class TutorialService {
  private readonly shepherdService = inject(ShepherdService);
  private readonly transloco = inject(TranslocoService);
  private readonly countryRepo = inject(CountryRepository);

  private activeCountry: CountryCode | undefined = undefined;

  constructor() {
    this.shepherdService.modal = true;

    combineLatest([
      this.countryRepo.activeCountry$.pipe(filterNil()),
      this.transloco
        .selectTranslateObject('TUTORIAL', {
          'EXPORT_FILTER.BODY.P_3': {
            exportLimitNumber: MAX_PRODUCTS_EXPORT_NUMBER,
          },
        })
        .pipe(retry(3), take(1)),
    ]).subscribe({
      next: ([activeCountry, translateObj]) => {
        this.activeCountry = activeCountry.country_code;

        const nextButton = {
          text: translateObj.OPTIONS.NEXT_LABEL,
          action: () => this.shepherdService.next(),
        };
        const prevButton = {
          text: translateObj.OPTIONS.PREV_LABEL,
          action: () => this.shepherdService.back(),
        };
        const doneButton = {
          text: translateObj.OPTIONS.DONE_LABEL,
          action: () => this.shepherdService.complete(),
        };

        this.shepherdService.defaultStepOptions = {
          scrollTo: {
            behavior: 'smooth',
            block: 'center',
          },
          cancelIcon: {
            enabled: true,
          },
          buttons: [prevButton, nextButton],
        };

        this.shepherdService.addSteps(
          this.getTutorialSteps(
            translateObj,
            prevButton,
            nextButton,
            doneButton,
          ),
        );
      },
    });
  }

  startTutorial(): void {
    this.shepherdService.start();
  }

  private getTutorialSteps(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TUTORIAL: any,
    prevButton: StepOptionsButton,
    nextButton: StepOptionsButton,
    doneButton: StepOptionsButton,
  ): StepOptions[] {
    // Use Switzerland-specific welcome message if active country is Switzerland
    const welcomeHeading =
      this.activeCountry === CountryCode.SWITZERLAND
        ? TUTORIAL.WELCOME_SCREEN.HEADING_CH
        : TUTORIAL.WELCOME_SCREEN.HEADING;

    return [
      {
        title: welcomeHeading,
        text: TUTORIAL.WELCOME_SCREEN.BODY,
        buttons: [nextButton],
      },
      {
        attachTo: {
          element: '.tutorial-step-faq',
          on: 'bottom',
        },
        text: TUTORIAL.FAQ,
        title: 'FAQ',
      },
      {
        attachTo: {
          element: '.tutorial-step-contrast',
          on: 'bottom',
        },
        text: TUTORIAL.THEME_SWITCH,
      },
      {
        attachTo: {
          element: '.tutorial-step-notifications',
          on: 'bottom',
        },
        text: TUTORIAL.NOTIFICATION_ICON,
      },
      {
        attachTo: {
          element: '.tutorial-step-languageSelect',
          on: 'bottom',
        },
        title: TUTORIAL.LANGUAGE_SELECTION.HEADING,
        text: `<h3><strong>${
          TUTORIAL.LANGUAGE_SELECTION.BODY.H3_1
        }</strong></h3>${
          this.activeCountry !== CountryCode.CZECH
            ? `<p>${TUTORIAL.LANGUAGE_SELECTION.BODY.P_1}</p>`
            : ''
        }<h3><strong>${TUTORIAL.LANGUAGE_SELECTION.BODY.H3_2}</strong></h3>${
          this.activeCountry !== CountryCode.CZECH
            ? `<p>${TUTORIAL.LANGUAGE_SELECTION.BODY.P_2}</p>`
            : ''
        }`,
      },
      {
        attachTo: {
          element: '.tutorial-step-filter',
          on: 'bottom',
        },
        text: `<p>${
          TUTORIAL.MAIN_FILTER_SELECTION
        }</p><img src="assets/images/tutorial/${
          this.activeCountry === CountryCode.CZECH
            ? 'filter-panel-cz.gif'
            : 'filter-panel.gif'
        }" width="100%">`,
      },
      {
        attachTo: {
          element: '.tutorial-step-search',
          on: 'bottom',
        },
        title: TUTORIAL.SEARCH_BAR.HEADING,
        text: `<p>${TUTORIAL.SEARCH_BAR.BODY}</p>`,
      },
      {
        attachTo: {
          element: '.tutorial-step-favFilters',
          on: 'left',
        },
        title: TUTORIAL.FAVORITE_FILTERS.HEADING,
        text: `<img src='assets/images/tutorial/${
          this.activeCountry === CountryCode.CZECH
            ? 'ffilters-2-cz.png'
            : 'ffilters-2.png'
        }' style="display: block; margin: 0 auto"><p>${
          TUTORIAL.FAVORITE_FILTERS.BODY.P_1
        }</p>
        <p>${TUTORIAL.FAVORITE_FILTERS.BODY.P_2}
          <ul>
            <li>${TUTORIAL.FAVORITE_FILTERS.BODY.LI_1}</li>
            <li>${TUTORIAL.FAVORITE_FILTERS.BODY.LI_2}</li>
          </ul>
        </p>
        <p>${TUTORIAL.FAVORITE_FILTERS.BODY.P_3}</p>`,
      },
      {
        attachTo: {
          element: '.tutorial-step-dataTabs',
          on: 'bottom',
        },
        text: TUTORIAL.DATA_TABS,
      },
      {
        attachTo: {
          element: '.tutorial-step-dateRange',
          on: 'right',
        },
        text: `<p>${TUTORIAL.DATE_PICKER.BODY}</p>
        <img src="assets/images/tutorial/${
          this.activeCountry === CountryCode.CZECH
            ? 'ffilters-datePicker-cz.png'
            : 'ffilters-datePicker-1.png'
        }"><br />
        <small>${TUTORIAL.DATE_PICKER.SMALL}</small>`,
      },
      {
        attachTo: {
          element: '.tutorial-step-sortBy',
          on: 'bottom',
        },
        text: TUTORIAL.SORT_BY_FILTER,
      },
      {
        attachTo: {
          element: '.tutorial-step-export',
          on: 'bottom',
        },
        text: `<p>${TUTORIAL.EXPORT_FILTER.BODY.P_1}</p>
               <p>${TUTORIAL.EXPORT_FILTER.BODY.P_2}</p>
               <p>${TUTORIAL.EXPORT_FILTER.BODY.P_3}</p>`,
      },
      {
        title: TUTORIAL.THE_END_OF_TUTORIAL.HEADING,
        text: TUTORIAL.THE_END_OF_TUTORIAL.BODY,
        buttons: [prevButton, doneButton],
      },
    ];
  }
}
